import { PerimetersService } from '../../../@core/utils/perimeters.service';
import { idMapToArray } from '../../../@core/utils/scope.service';
import { InvestmentHistoryDialogComponent } from '../investment-history-dialog/investment-history-dialog.component';
import { AssetsService } from '../../../@core/utils/assets.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { combineLatest, forkJoin, of, Subscription, Observable } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { KPI } from '../../../@core/api';
import {
  ConfigService,
  DocumentsService,
  I18nService,
  InitiativesService,
  InvestmentReasonsService,
  InvestmentsChangesService,
  InvestmentsService,
  NomenclatureService,
  PriceSheetService,
  RoadmapService,
  ScopeService,
  StateLevelsService,
  UsersService,
} from '../../../@core/utils';
import { LightgalleryModule } from 'lightgallery/angular';
import { LightGallery } from 'lightgallery/lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import { NbDialogService, NbCardModule, NbButtonModule, NbListModule } from '@nebular/theme';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { AgGridDocumentsComponent, SheetDialogComponent } from '..';
import { LinkableInvestmentsListComponent } from '../side-forms/linkable-investments-list/linkable-investments-list.component';
import { IdNameMixin, RequireAtLeastOne } from '../../../@core/api/models';
import { InvestmentFormComponent } from '../side-forms/investment-form/investment-form.component';
import { MappedInvestmentDocuments } from '../../../@core/utils/investments.service';
import { InvestmentLocationPipe } from '../../pipes/investment-location.pipe';
import { NextAssetItemComponent } from '../next-asset-item/next-asset-item.component';
import { LinkedEquipmentDialogComponent } from '../linked-equipment-dialog/linked-equipment-dialog.component';
import { PriceSheetsListComponent } from '../price-sheets-list/price-sheets-list.component';
import { LifecycleChartComponent } from '../charts/lifecycle-chart/lifecycle-chart.component';
import { BudgetMonitoringChartComponent } from '../charts/budget-monitoring-chart/budget-monitoring-chart.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { NgStyle, NgClass, DecimalPipe, AsyncPipe } from '@angular/common';
import { LightGalleryCoreSettings } from 'lightgallery/lg-settings';
import { LIGHT_GALLERY_LICENSE_KEY } from '../../../../license-keys';

@Component({
  selector: 'app-investment-sheet',
  templateUrl: './investment-sheet.component.html',
  styleUrls: ['./investment-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    
    BreadcrumbsComponent,
    NbCardModule,
    NgStyle,
    NbButtonModule,
    NbListModule,
    RouterLink,
    FontAwesomeModule,
    BudgetMonitoringChartComponent,
    NgClass,
    LifecycleChartComponent,
    PriceSheetsListComponent,
    NextAssetItemComponent,
    DecimalPipe,
    TranslateModule,
    InvestmentLocationPipe,
    LightgalleryModule,
    AsyncPipe
  ],
})
export class InvestmentSheetComponent implements OnDestroy, OnInit {

  @Input() public investmentId: number;
  @Input() public minimizedView: boolean;

  public budgetYear: number[];
  public projects: KPI.Project[] = [];
  public investment: KPI.MappedInvestment;
  public originalInvestment: KPI.MappedInvestment;
  public tasks: KPI.MappedTask[];
  public assetSuperCategory: KPI.SuperCategory;
  public assetCategory: KPI.Category;
  public assetSubCategory: KPI.SubCategory;
  public assetType: KPI.AssetType;
  public initiative: KPI.MappedInitiative;
  public perimeter: KPI.Perimeter;
  public currency: string = this.i18n.getCurrency();
  public energyEnabled: boolean = false;
  public kWhSavingsTotal: number = 0;
  public canAccessEnergy: boolean;
  public initiativesTabActive: boolean = false;
  public history: KPI.YearAndStatusHistoryItem[] = [];
  public roadmapIndicators: KPI.RoadmapIndicator[] = [];
  public budget: string = '';
  public technicalStateLabel: string;
  public workflowReasons: KPI.InvestmentReason[] = [];
  public furtherInformations: IdNameMixin[] = [];
  public docCount: number;
  public seeMore: boolean = false;
  public customerConfig$ = this.config.customerConfig$;
  private fieldOptions: { [key: string]: { id: number, name: string }[] } = {
    priority: [],
    status: [],
  };
  public nbLinkedInvest: number = 0;
  public nextAsset: KPI.MappedAsset;
  public priceSheets: KPI.PriceSheet[] = [];
  public scopesString: string = '-';
  public recipientsString: string = '-';
  public sponsorsString: string = '-';
  public investmentCodeFieldName: string;
  public readOnly: boolean = false;
  public customerConfig: KPI.CustomerConfig = null;

  /**
   * Image gallery settings
   */
  public settings: RequireAtLeastOne<LightGalleryCoreSettings> = {
    counter: false,
    speed: 500,
    plugins: [lgZoom, lgThumbnail],
    dynamic: true,
    dynamicEl: [],
    licenseKey: LIGHT_GALLERY_LICENSE_KEY,
    zoomFromOrigin: true,
  };

  private lightGallery!: LightGallery;

  public onInit = (detail): void => {
    this.lightGallery = detail.instance;
  };

  public openGallery = () => {
    this.lightGallery.openGallery();
  };

  private subscriptions: Subscription[] = [];
  public linkedInvestments$: Observable<MappedInvestmentDocuments[]>;

  constructor(
    private route: ActivatedRoute,
    private i18n: I18nService,
    private investmentsService: InvestmentsService,
    private nomenclatureService: NomenclatureService,
    private translate: TranslateService,
    private scopeService: ScopeService,
    private investmentReasonsService: InvestmentReasonsService,
    private initiativesService: InitiativesService,
    private stateLevelService: StateLevelsService,
    private assetsService: AssetsService,
    private dialogService: NbDialogService,
    private perimetersService: PerimetersService,
    private router: Router,
    private investmentsChangesService: InvestmentsChangesService,
    private cdRef: ChangeDetectorRef,
    private roadmapService: RoadmapService,
    private priceSheetService: PriceSheetService,
    private documentsService: DocumentsService,
    private config: ConfigService,
    private usersService: UsersService,
    private rightPanelDialogService: RightPanelDialogService,
  ) { }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.scopeService.user$.subscribe((user) => this.energyEnabled = user.can_access_energy),
      this.config.customerConfig$.subscribe((config) => {
        this.customerConfig = config;
        if (config.investment_code_field_name) {
          this.investmentCodeFieldName = config.investment_code_field_name;
        } else {
          this.investmentCodeFieldName = this.translate.instant('Customize code');
        }
      }),
      this.route.paramMap.pipe(
        filter((paramMap) => !!this.investmentId || paramMap.has('id')),
        map((paramMap) => (this.investmentId && this.minimizedView ? this.investmentId : null) || paramMap.get('id')),
        switchMap((id) =>
          combineLatest([
            this.investmentsChangesService.pendingChangesNumber$,
            this.investmentsService.reloadInvestment$,
          ]).pipe(
            filter(([pendingChangesNumber]) => !pendingChangesNumber),
            switchMap(() =>
              this.investmentsService.getInvestment(Number(id)).pipe(
                first(),
              )
            ),
            switchMap((investment) => {
              let originalInvestment: Observable<MappedInvestmentDocuments> = of(null);
              if (investment?.original_investment) {
                originalInvestment = this.investmentsService.getInvestment(investment.original_investment) as Observable<MappedInvestmentDocuments>;
              }
              return combineLatest([
                of(investment),
                originalInvestment,
                this.investmentReasonsService.investmentReasons$,
                this.scopeService.investmentPriorities$.pipe(idMapToArray()),
                this.perimetersService.perimeters$,
                this.scopeService.user$,
                this.roadmapService.roadmapIndicators(),
                this.scopeService.getInvestFurtherInfo$.pipe(first(), idMapToArray()),
                this.scopeService.budgetScopes$.pipe(first(), idMapToArray()),
                this.scopeService.investmentSponsors$.pipe(idMapToArray()),
                this.scopeService.investmentInvoicesRecipient$.pipe(idMapToArray()),
                this.usersService.currentUserPerimeterPermissions$,
              ]);
            })
          )),
        tap(([
          investment,
          originalInvestment,
          reasons,
          priorities,
          perimeters,
          user,
          roadmapIndicators,
          furtherInformations,
          scopes,
          sponsors,
          recipients,
          permissions
        ]) => {
          this.originalInvestment = originalInvestment
          this.canAccessEnergy = user.can_access_energy
          this.fieldOptions.priority = priorities;
          this.workflowReasons = reasons;
          this.furtherInformations = furtherInformations
          this.investment = {
            ...investment as MappedInvestmentDocuments,
            initiative: originalInvestment ? originalInvestment.initiative : investment.initiative,
          };

          if (!!investment.budget_scopes?.length) {
            this.scopesString = scopes.filter((bs) =>
              investment.budget_scopes.includes(bs.id)
            ).map(bs => bs.name).join(' - ');
          }

          if (!!investment.sponsorships.length) {
            const sponsorMap = new Map(sponsors.map((s) => [s.id, s.name]));
            this.sponsorsString = investment.sponsorships.map((sp) => {
              const name = sponsorMap.get(sp.sponsor.id);
              return `${name} : ${sp.percentage}%`;
            })
            .join('<br>');
          }

          if (!!this.investment.invoicings.length) {
            const recipientsMap = new Map(recipients.map((r) => [r.id, r.name]));
            this.recipientsString = this.investment.invoicings.map((re) => {
              const name = recipientsMap.get(re.recipient.id);
              return `${name} : ${re.percentage}%`;
            })
            .join('<br>');
          }

          // Roadmap indicators
          this.roadmapIndicators = roadmapIndicators;

          // Perimeter
          this.perimeter = (investment as MappedInvestmentDocuments).perimeters.find((p) => !p.is_monosite);
          const monoPerimeter = (investment as MappedInvestmentDocuments).perimeters.find((p) => p.is_monosite);

          // Initiative
          if (monoPerimeter) {
            this.subscriptions.push(
              this.initiativesService.getInitiatives([monoPerimeter]).subscribe(initiatives => {
                this.initiative = initiatives.find(initiative => initiative.investment?.id === this.investment.id)
                this.cdRef.detectChanges();
              })
            )
          }

          // Read only
          const relevantPerimeter = this.investment.perimeters.find((p) => !!p.level_parent === false)
          if (relevantPerimeter) {
            const relevantPerimeterPerm = permissions.find((perm) => perm.perimeter === relevantPerimeter.id)
            this.readOnly = relevantPerimeterPerm?.read_only || !this.customerConfig?.is_show_edit
          }

          if (this.perimeter && perimeters.find((p) => p.id === this.perimeter.id)) {
            this.subscriptions.push(
              this.investmentsService.getInvestmentPictures(this.investment).subscribe((pictures) => {
                this.settings.dynamicEl = pictures.map((p) => ({
                  id: p.id,
                  src: 'https://www.agexea.com/wp-content/uploads/2015/05/rt-2012.jpg',
                  thumb: 'https://www.agexea.com/wp-content/uploads/2015/05/rt-2012.jpg',
                }));
                this.cdRef.detectChanges();
              }),
            );
            // this.loadLinkedInvestments();
            this.loadPriceSheets();
            this.getAssetStateLabel();
            this.getDocumentsCount();

            //budget year
            this.budgetYear = investment.slices.filter((s) =>
              s.status === investment.status
            ).sort((a, b) => a.year - b.year).map((s) =>
              s.year
            );

            this.budget = investment.slices.filter((s) =>
              s.status === investment.status
            ).sort((a, b) => a.year - b.year).map((s) =>
              this.i18n.formatCurrency(Number(s.price))
              + ' '
              + this.translate.instant('pages.energy.initiative.labels.in')
              + ' '
              + s.year
            ).join(', ');

            this.cdRef.detectChanges();
          } else {
            this.router.navigate(['decision', 'overview']);
          }
          const linkedToId = (investment as any).linked_to;
          if (linkedToId) {
            // Secondary: show main + siblings, exclude self
            this.linkedInvestments$ = forkJoin([
              this.investmentsService.getInvestment(linkedToId) as Observable<any>,
              this.investmentsService.getLinkedInvestments(linkedToId) as Observable<any[]>,
            ]).pipe(
              map(([mainInv, siblings]) =>
                ([mainInv, ...siblings] as any[]).filter(inv => inv?.id !== investment.id)
              )
            );
          } else {
            this.linkedInvestments$ = this.investmentsService.getLinkedInvestments(investment.id);
          }

        }),
        tap(() => {
          if (!!this.investment?.initiative?.initiative_type) {
            this.initiativesService.getInitiative(
              this.investment.initiative.id
            ).subscribe((initiative) => {
              this.initiative = initiative;
              this.cdRef.detectChanges();
            });
          }
        }),

        // Retrieve asset category, sub-category and type for breadcrumbs
        tap(() => {
          if (this.investment.asset) {
            this.nomenclatureService.assetTypes$.pipe(
              map((assetTypes) => assetTypes.find((assetType) => assetType.id === this.investment.asset_object.asset_type)),
            ).subscribe((assetType) => {
              this.assetSuperCategory = assetType.super_category;
              this.cdRef.detectChanges();
            });
          }
        }),
        switchMap(([investment]) => {
          if (investment.asset) {
            return this.assetsService.getAssetsPictures([investment.asset])
          } else {
            return of([]);
          }
        }),
      ).subscribe((pictures) => {
        if (pictures && pictures.length) {
          this.settings.dynamicEl.push(
            ...pictures.map((p) => ({
              id: p.id,
              src: p.picture,
              thumb: p.thumbnail,
            })),
          );
        }
        this.cdRef.detectChanges();
      }),
    );
  }


  public openHistory(): void {
    this.dialogService.open(InvestmentHistoryDialogComponent, {
      context: {
        investmentId: this.investment.id,
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public openInvestmentForm() {
    this.rightPanelDialogService.open(
      InvestmentFormComponent,
      {
        closeOnBackdropClick: false,
        context: {
          investment: this.investment,
        },
      },
    )
  }

  public seeInitiative(initiativeId: number): void {
    this.dialogService.open(
      SheetDialogComponent,
      {
        autoFocus: false,
        context: { initiativeId },
      },
    );
  }

  private getAssetStateLabel(): void {
    if (this.investment.asset) {
      this.subscriptions.push(
        this.assetsService.getAssetLifecycle(this.investment.asset).subscribe((lifecycle) => {
          this.technicalStateLabel = this.stateLevelService.getTechnicalStateLabelFromNote(lifecycle.current_year_note);
          this.cdRef.detectChanges();
        }),
      )
    }
  }

  /**
  * Load the price sheets linked to investment's asset asset type if any
  */
  private loadPriceSheets(): void {

    if (this.investment?.next_asset) {
      this.subscriptions.push(
        this.assetsService.getAsset(this.investment.next_asset).pipe(
          switchMap((asset) => {
            this.nextAsset = asset;
            return this.priceSheetService.getPriceSheetsByAssetType(
              this.nextAsset.asset_type_object.id
            )
          })
        ).subscribe((priceSheets) => {
          if (priceSheets?.length) {
            this.priceSheets = priceSheets;
            this.cdRef.detectChanges();
          }
        }),
      );
    } else if (this.investment?.asset_object) {
      this.subscriptions.push(
        this.priceSheetService.getPriceSheetsByAssetType(
          this.investment?.asset_object.asset_type_object?.id
        ).subscribe((priceSheets) => {
          if (priceSheets?.length) {
            this.priceSheets = priceSheets;
            this.cdRef.detectChanges();
          }
        }
        ),
      );
    }
  }

  public openAsset(assetId: number) {
    this.dialogService.open(SheetDialogComponent, { autoFocus: false, context: { assetId } });
  }

  public openLinkedEquipmentDialog(): void {
    this.rightPanelDialogService.open(LinkedEquipmentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      context: {
        linkedEquipmentIds: this.investment.asset ? [this.investment.asset] : [],
        siteId: this.perimeter?.id || null,
        title: this.translate.instant('Equipment related to the investment'),
        investmentId: this.investment.id,
        multipleChoice: false,
        readOnly: this.readOnly,
        investmentType: this.investment.investment_type_object,
      },
    });
  }

  public indicatorInInvest(indicatorId: number): boolean {
    return this.investment.impact_roadmap_indicators.includes(indicatorId);
  }

  public reasonInInvest(reasonId: number): boolean {
    return this.investment.reasons.includes(reasonId);
  }

  public infoInInvest(infoId: number): boolean {
    return this.investment.further_information?.id === infoId;
  }

  private getDocumentsCount(): void {
    this.subscriptions.push(
      this.documentsService.getDocumentsCount(
        [],
        [this.investment.id],
        [],
        []
      ).subscribe((docCount) => {
        this.docCount = docCount;
      })
    )
  }

  public seeDocuments(): void {
    this.dialogService.open(
      AgGridDocumentsComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('the invest'),
          assetIds: [],
          investIds: [this.investment.id],
          actionIds: [],
        }
      }
    );
  }

  public seeMoreToggle(): void {
    this.seeMore = !this.seeMore;
    this.cdRef.detectChanges();
  }

  public getInitiativeSavings(): number {
    return this.initiative?.gains?.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0) || 0;
  }

  public openLinkedInvestmentsPanel(): void {
    const linkedToId = (this.investment as any).linked_to;
    this.rightPanelDialogService.open(LinkableInvestmentsListComponent, {
      autoFocus: false,
      context: {
        mainInvestmentId: this.investment.id,
        linkedToId: linkedToId || null,
        readOnly: false,
        canLink: false,
        multipleChoice: false,
        startYear: 1950,
        endYear: 2100,
      },
    });
  }
}
